import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import moment from 'moment';

import { Typography } from '@universal-tennis/ui-shared';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect from '@mui/material/Select';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';

import DatePicker from '../DatePicker';
import FullPageModal from './FullPageModal';
import Input from '../Form/Input';
import LoadingIndicator from '../LoadingIndicator';
import Location from '../Form/Location';
import Popup from '../Popups/Popup';
import Select from '../Form/Select';
import SessionMatchFormat from '../SessionMatchFormat';
import TimePicker from '../Form/TimePicker';
import { CustomSwitch } from '../Styled';

import { getTooManyMatchesPerTeamError } from '../../utils/sessions';

import {
    FrequencyTypes,
    LeagueSessionTypes,
    MatchFormatTypes,
    SessionDrawTypes,
    SessionStandingsRules,
    SessionStandingsTypes,
    StatusTypes,
    TournamentTeamsCount,
    drawTypes,
    matchFrequencyTypes,
    sessionMatchFormatTypes,
    sessionTypes,
    tennisSessionStandingsOptions,
    pickleballSessionStandingsOptions,
    SportTypeIds,
} from '../../utils/types';
import { defaultGridSpacing, SharedUICategories, TypographySizes } from '../../utils/constants';
import { showErrorPopup } from '../../redux/errorPopupSlice';
import { getLocationData } from '../../utils/location';
import { useUpdateSessionMutation } from '../../api/leaguesApi';
import { combineDateAndTime, splitDateAndTime } from '../../utils/display';
import { setSportTypeId } from '../../redux/conferenceDetailsSlice';
import { manageSessionModalPropTypes } from '../../utils/proptypes';
import { useStatsig } from '../../hooks/StatsigContextProvider';
import { getTournamentTeamSizeOptions } from '../../utils/helpers';
import { formatMoney } from '../../utils/money';

const CUSTOM_MORE_MATCHES_TEXT = 'There are more matches per team than weeks in the session.';

const ManageSessionModal = ({ sessionDetails = {}, isLoadingSession = false, title = '', isOpen = false, onClose, onSaveClick }) => {
    const params = useParams();
    const theme = useTheme();
    const { statsig } = useStatsig();
    const { SECONDARY } = SharedUICategories;
    const { X_SMALL_MEDIUM_CAP, SMALL_BOOK, X_SMALL_BOOK, MEDIUM_BOOK } = TypographySizes;
    const dispatch = useDispatch();
    const { sportTypeId } = useSelector((state) => state.conferenceDetails);

    const sessionId = params?.sessionId;
    const { OPEN, CLOSED } = StatusTypes;
    const { ONCE_A_WEEK } = FrequencyTypes;
    const { TOURNAMENT } = LeagueSessionTypes;
    const { FIRST_MATCH_BACK_DRAW } = SessionDrawTypes;
    const { BOTH, DOUBLES, SINGLES } = MatchFormatTypes;
    const { FOUR, EIGHT, SIXTEEN, THIRTY_TWO, SIXTY_FOUR, ONE_TWENTY_EIGHT } = TournamentTeamsCount;
    const { TENNIS_WIN_LOSS_RECORD, PICKLEBALL_WIN_LOSS_RECORD } = SessionStandingsTypes;
    const { hasRegisteredPlayers, hasPostedResults, hasPublishedSchedule, hasSchedule } = sessionDetails || {};
    const isRestrictedSession = hasPostedResults && hasPublishedSchedule;
    const canRegister = sessionDetails?.sessionStatusTypeId === OPEN;
    const isTennisSession = sportTypeId === SportTypeIds.TENNIS;
    const standingsRuleOptions = isTennisSession ? tennisSessionStandingsOptions : pickleballSessionStandingsOptions;
    const isLargeTournamentSizesEnabled = statsig?.featureGates?.largeTournaments;
    const tournamentTeamCountOptions = getTournamentTeamSizeOptions(isLargeTournamentSizesEnabled);

    const [sessionRegistrationStatus, setSessionRegistrationStatus] = useState(true);
    const [updatedSessionRegistrationStatus, setUpdatedSessionRegistrationStatus] = useState(OPEN);
    const [totalPlayerCost, setTotalPlayerCost] = useState();
    const [matchFormat, setMatchFormat] = useState('');
    const [originalLocation, setOriginalLocation] = useState();
    const [originalFormData, setOriginalFormData] = useState();
    const [isTournamentSession, setIsTournamentSession] = useState(false);
    const [conferenceId, setConferenceId] = useState(parseInt(params?.conferenceId) || skipToken);
    const [leagueVerifiedFee, setLeagueVerifiedFee] = useState(0);
    const [showRuleConfirmationModal, setShowRuleConfirmationModal] = useState(false);
    const [sessionStandingsRuleId, setSessionStandingsRuleId] = useState(
        isTennisSession ? TENNIS_WIN_LOSS_RECORD : PICKLEBALL_WIN_LOSS_RECORD
    );
    const [updatedStandingsRuleId, setUpdatedStandingsRuleId] = useState(null);

    const registrationStatusDescription = sessionRegistrationStatus ? 'Open' : 'Closed';
    const [updateSession] = useUpdateSessionMutation();

    const defaultTime = moment().startOf('day').set({ hour: 9 });
    const defaultStartDate = moment().add(3, 'days').startOf('day');
    const defaultEndDate = moment().add(5, 'days').startOf('day');
    const defaultRegistrationEndDate = moment().add(2, 'days').startOf('day');
    const defaultRegistrationStartDate = moment().startOf('day');
    const defaultRegistrationStartTime = moment().startOf('day').set({ hour: 11 });

    const defaultValues = {
        description: '',
        doublesMatchCount: '',
        doublesPointsPerMatch: '',
        doublesScoringFormatTypeId: '',
        doublesTimeAllowedTypeId: '',
        drawTypeId: FIRST_MATCH_BACK_DRAW,
        endDateUtc: defaultEndDate,
        endDateTime: defaultTime,
        matchesPerTeam: '',
        name: '',
        registrationFee: 0,
        registrationVerificationFee: 0,
        sessionStatusTypeId: OPEN,
        sessionDetails: SINGLES,
        sessionTypeId: '',
        singlesMatchCount: '',
        singlesPointsPerMatch: '',
        singlesScoringFormatTypeId: '',
        singlesTimeAllowedTypeId: '',
        startDateUtc: defaultStartDate,
        startDateTime: defaultTime,
        teamRegistrationEndDateUtc: defaultRegistrationEndDate,
        registrationEndTime: defaultTime,
        teamRegistrationStartDateUtc: defaultRegistrationStartDate,
        registrationStartTime: defaultRegistrationStartTime,
        teamRequiredPlayersMin: 2,
        teamsMax: '',
        location: '',
        teamMatchFrequencyTypeId: ONCE_A_WEEK,
    };

    const {
        handleSubmit,
        formState: { errors, dirtyFields },
        control,
        reset,
        getValues,
        setValue,
        setError,
        clearErrors,
    } = useForm({ defaultValues, mode: 'all' });

    useEffect(() => {
        if (sessionDetails) {
            const {
                conferenceId: sessionConferenceId,
                endDateUtc,
                startDateUtc,
                registrationFee,
                teamRegistrationStartDateUtc,
                teamRegistrationEndDateUtc,
                registrationVerificationFee,
                sessionMatchTypeId,
                sessionTypeId,
                sessionStandingsTypeId,
                sessionStatusTypeId,
                description,
                drawTypeId,
                ...fetchedSession
            } = sessionDetails;

            setConferenceId(sessionConferenceId);
            setSessionStandingsRuleId(sessionStandingsTypeId ?? sessionStandingsRuleId);

            let sessionLocation = '';
            const isTournament = sessionTypeId === TOURNAMENT;
            if (isTournament) {
                sessionLocation = sessionDetails?.location?.placeName || '';
                setOriginalLocation(sessionDetails?.location);
                setIsTournamentSession(true);
            }

            let formData;

            const { date: endDate, time: endTime } = splitDateAndTime(endDateUtc);
            const { date: startDate, time: startTime } = splitDateAndTime(startDateUtc);
            const { date: registrationEndDate, time: registrationEndTime } = splitDateAndTime(teamRegistrationEndDateUtc);
            const { date: registrationStartDate, time: registrationStartTime } = splitDateAndTime(teamRegistrationStartDateUtc);

            if (sessionMatchTypeId !== BOTH) {
                formData = {
                    ...defaultValues,
                    ...fetchedSession,
                    sessionTypeId,
                    drawTypeId,
                    description: description || '',
                    registrationFee,
                    sessionStatusTypeId,
                    registrationVerificationFee,
                    sessionMatchTypeId,
                    location: sessionLocation,
                    endDateUtc: endDate,
                    endDateTime: endTime,
                    startDateUtc: startDate,
                    startDateTime: startTime,
                    teamRegistrationStartDateUtc: registrationStartDate,
                    registrationStartTime,
                    teamRegistrationEndDateUtc: registrationEndDate,
                    registrationEndTime
                };
            } else {
                formData = {
                    ...fetchedSession,
                    drawTypeId,
                    registrationFee,
                    registrationVerificationFee,
                    sessionMatchTypeId,
                    sessionStatusTypeId,
                    sessionTypeId,
                    description: description || '',
                    location: sessionLocation,
                    endDateUtc: endDate,
                    endDateTime: endTime,
                    startDateUtc: startDate,
                    startDateTime: startTime,
                    teamRegistrationStartDateUtc: registrationStartDate,
                    registrationStartTime,
                    teamRegistrationEndDateUtc: registrationEndDate,
                    registrationEndTime
                };
            }

            setSessionRegistrationStatus(canRegister);
            setTotalPlayerCost((parseFloat(registrationFee) || 0) + (parseFloat(registrationVerificationFee) || 0));
            setLeagueVerifiedFee(parseFloat(registrationVerificationFee) || 0);
            setOriginalFormData(formData);
            reset(formData);
            setMatchFormat(sessionMatchTypeId);
            dispatch(setSportTypeId(sessionDetails?.sportTypeId));
        }
    }, [sessionDetails]);

    const onHandleRegistrationStatusSwitch = () => {
        const newId = updatedSessionRegistrationStatus === OPEN ? CLOSED : OPEN;
        setSessionRegistrationStatus((prevStatus) => !prevStatus);
        setUpdatedSessionRegistrationStatus(newId);
    };

    const handleFormSubmit = async (formValues) => {
        const session = { ...formValues };

        const combinedStartDate = combineDateAndTime(session.startDateUtc, session.startDateTime);
        const combinedEndDate = combineDateAndTime(session.endDateUtc, session.endDateTime);
        const combinedRegistrationStartDate = combineDateAndTime(session.teamRegistrationStartDateUtc, session.registrationStartTime);
        const combinedRegistrationEndDate = combineDateAndTime(session.teamRegistrationEndDateUtc, session.registrationEndTime);

        session.startDateUtc = combinedStartDate.toISOString();
        session.endDateUtc = combinedEndDate.toISOString();
        session.teamRegistrationStartDateUtc = combinedRegistrationStartDate.toISOString();
        session.teamRegistrationEndDateUtc = combinedRegistrationEndDate.toISOString();
        session.description = session.description.trim().length === 0 ? null : session.description;
        session.sessionMatchTypeId = matchFormat;
        session.sessionStatusTypeId = updatedSessionRegistrationStatus;
        session.sessionStandingsTypeId = sessionStandingsRuleId;

        delete session.endDateTime;
        delete session.registrationEndTime;
        delete session.registrationStartTime;
        delete session.startDateTime;

        if (matchFormat === SINGLES) {
            delete session.doublesMatchCount;
            delete session.doublesPointsPerMatch;
            delete session.doublesScoringFormatTypeId;
            delete session.doublesTimeAllowedTypeId;
        }

        if (matchFormat === DOUBLES) {
            delete session.singlesMatchCount;
            delete session.singlesPointsPerMatch;
            delete session.singlesScoringFormatTypeId;
            delete session.singlesTimeAllowedTypeId;
        }

        const sessionType = getValues('sessionTypeId');

        if (sessionType === TOURNAMENT) {
            delete session.teamMatchFrequencyTypeId;

            if (dirtyFields.location) {
                session.location = await getLocationData(formValues.location);
            } else {
                session.location = originalLocation;
            }
        } else {
            delete session.location;
        }

        try {
            await updateSession({ sessionId, session, conferenceId }).unwrap();
        } catch (apiError) {
            dispatch(showErrorPopup(apiError));
        }
    };

    const handleEntryFeeChange = (e) => {
        const feeValue = parseFloat(e.target.value);
        if (!Number.isNaN(feeValue)) {
            setTotalPlayerCost(feeValue + leagueVerifiedFee);
        } else {
            setTotalPlayerCost(leagueVerifiedFee);
        }
    };

    const handleOnMatchFormatChange = (event) => {
        setMatchFormat(event.target.value);
    };

    const handleOnSessionTypeChange = (event) => {
        const isTournament = event.target.value === TOURNAMENT;
        setIsTournamentSession(isTournament);
    };

    const handleMatchesPerTeamCheck = (event) => {
        clearErrors('matchesPerTeam');
        setValue('matchesPerTeam', event.target.value);
        const session = getValues();

        const hasErrors = getTooManyMatchesPerTeamError({
            ...session,
            matchesPerTeam: +event.target.value,
        });

        if (hasErrors) {
            setError('matchesPerTeam', { type: 'custom', message: CUSTOM_MORE_MATCHES_TEXT });
        }
    };

    const handleOnTournamentTeamsChange = (event) => {
        const teamsCount = event.target.value;
        switch (teamsCount) {
            case FOUR:
                setValue('matchesPerTeam', 2);
                break;
            case EIGHT:
                setValue('matchesPerTeam', 4);
                break;
            case SIXTEEN:
                setValue('matchesPerTeam', 8);
                break;
            case THIRTY_TWO:
                setValue('matchesPerTeam', 16);
                break;
            case SIXTY_FOUR:
                setValue('matchesPerTeam', 32);
                break;
            case ONE_TWENTY_EIGHT:
                setValue('matchesPerTeam', 64);
                break;
            default:
                break;
        }
    };

    const handleOnSave = () => {
        handleSubmit(handleFormSubmit)();
        onSaveClick();
    };

    const handleOnClose = () => {
        setSessionRegistrationStatus(canRegister);
        setMatchFormat(sessionDetails?.sessionMatchTypeId);
        reset(originalFormData);
        onClose();
    };

    const handleOnSessionRuleChange = (event) => {
        const ruleValue = event?.target?.value;

        if (ruleValue !== sessionStandingsRuleId) {
            setShowRuleConfirmationModal(true);
            setUpdatedStandingsRuleId(ruleValue);
        }
    };

    const handleOnConfirmRuleChange = () => {
        setShowRuleConfirmationModal(false);
        setSessionStandingsRuleId(updatedStandingsRuleId);
    };

    const handleOnCloseRuleModal = () => {
        setShowRuleConfirmationModal(false);
        setUpdatedStandingsRuleId(null);
    };

    return (
        <FullPageModal isOpen={isOpen} title={title} ctaTitle="Save" onClose={handleOnClose} onCtaClick={handleOnSave}>
            <Grid justifyContent="center" mt={3} mb={10}>
                {isLoadingSession ? (
                    <LoadingIndicator />
                ) : (
                    <form>
                        <Typography mb={2} category={SECONDARY} size={X_SMALL_MEDIUM_CAP}>
                            General
                        </Typography>
                        <Paper sx={{ p: 2, mb: 4, boxShadow: 'none' }}>
                            <Grid container spacing={defaultGridSpacing}>
                                <Grid item xs={12} lg={4}>
                                    <Input name="name" label="Session Name" isRequired hasError={!!errors.name} control={control} />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <Select
                                        isRequired
                                        disabled={isRestrictedSession}
                                        hasError={!!errors.sessionTypeId}
                                        name="sessionTypeId"
                                        label="Session Type"
                                        labelId="session-type-label"
                                        options={sessionTypes}
                                        control={control}
                                        onSelectChange={handleOnSessionTypeChange}
                                        isFullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    {!isTournamentSession && (
                                        <FormControl fullWidth>
                                            <InputLabel id="session-standings-rules-label">Team Standings Rule</InputLabel>
                                            <MuiSelect
                                                onChange={handleOnSessionRuleChange}
                                                value={sessionStandingsRuleId}
                                                label="Team Standings Rule"
                                                labelId="session-standings-rules-label"
                                            >
                                                {standingsRuleOptions.map((rule) => (
                                                    <MenuItem key={rule?.id} value={rule?.id}>
                                                        {rule?.description}
                                                    </MenuItem>
                                                ))}
                                            </MuiSelect>
                                        </FormControl>
                                    )}
                                    {isTournamentSession && (
                                        <Select
                                            disabled={hasSchedule}
                                            name="drawTypeId"
                                            label="Draw Type"
                                            labelId="draw-type-label"
                                            options={drawTypes}
                                            control={control}
                                            isFullWidth
                                        />
                                    )}
                                </Grid>
                            </Grid>
                            {isTournamentSession && (
                                <Grid my={1} container spacing={defaultGridSpacing}>
                                    <Grid item xs={12}>
                                        <Location
                                            name="location"
                                            label="Team Tournament Location"
                                            error={!!errors.location}
                                            control={control}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            {!isTournamentSession && (
                                <Grid my={1} container spacing={defaultGridSpacing}>
                                    <Grid item xs={12}>
                                        <Select
                                            disabled={isRestrictedSession}
                                            name="teamMatchFrequencyTypeId"
                                            label="Match Frequency"
                                            labelId="match-frequency-type-label"
                                            options={matchFrequencyTypes}
                                            control={control}
                                            isFullWidth
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            <Grid my={1} container spacing={defaultGridSpacing}>
                                <Grid item xs={12} lg={3}>
                                    <DatePicker
                                        name="startDateUtc"
                                        label="Session Start Date"
                                        control={control}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <TimePicker name="startDateTime" label="Session Start Time" control={control} />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <DatePicker
                                        name="endDateUtc"
                                        label="Session End Date"
                                        control={control}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <TimePicker name="endDateTime" label="Session End Time" control={control} />
                                </Grid>
                            </Grid>
                            <Grid my={1} container spacing={defaultGridSpacing}>
                                <Grid item xs={12}>
                                    <Input
                                        control={control}
                                        name="description"
                                        multiline
                                        minRows={3}
                                        hasError={!!errors.description}
                                        label="Description"
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                        <Typography mb={2} category={SECONDARY} size={X_SMALL_MEDIUM_CAP}>
                            Team Match Format
                        </Typography>
                        <Paper sx={{ p: 2, mb: 4, boxShadow: 'none' }}>
                            <Grid container alignItems="center" spacing={defaultGridSpacing}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="match-format-label">Format Type</InputLabel>
                                        <MuiSelect
                                            disabled={isRestrictedSession}
                                            onChange={handleOnMatchFormatChange}
                                            value={matchFormat}
                                            label="Format Type"
                                            labelId="match-format-label"
                                        >
                                            {sessionMatchFormatTypes.map((session) => (
                                                <MenuItem key={session.id} value={session.id}>
                                                    {session.description}
                                                </MenuItem>
                                            ))}
                                        </MuiSelect>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <SessionMatchFormat
                                sportTypeId={sportTypeId}
                                isDisabled={isRestrictedSession}
                                matchFormat={matchFormat}
                                control={control}
                                errors={errors}
                            />
                        </Paper>
                        <Typography mb={2} category={SECONDARY} size={X_SMALL_MEDIUM_CAP}>
                            Team Registration
                        </Typography>
                        <Paper sx={{ p: 2, mb: 4, boxShadow: 'none' }}>
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography category={SECONDARY} size={SMALL_BOOK}>
                                        Session Status
                                    </Typography>
                                    <Typography category={SECONDARY} size={X_SMALL_BOOK}>
                                        Teams can be added to an open session
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography category={SECONDARY} size={SMALL_BOOK}>
                                        {registrationStatusDescription}
                                    </Typography>
                                    <CustomSwitch
                                        name="sessionStatusTypeId"
                                        control={control}
                                        checked={sessionRegistrationStatus}
                                        inputProps={{ 'aria-label': `Switch` }}
                                        onChange={onHandleRegistrationStatusSwitch}
                                    />
                                </Box>
                            </Grid>
                            <Grid my={1} container spacing={defaultGridSpacing}>
                                <Grid item xs={12} lg={3}>
                                    <DatePicker
                                        name="teamRegistrationStartDateUtc"
                                        label="Registration Start Date"
                                        control={control}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <TimePicker name="registrationStartTime" label="Registration Start Time" control={control} />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <DatePicker
                                        name="teamRegistrationEndDateUtc"
                                        label="Registration End Date"
                                        control={control}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <TimePicker name="registrationEndTime" label="Registration End Time" control={control} />
                                </Grid>
                            </Grid>
                            <Grid my={1} container spacing={defaultGridSpacing}>
                                <Grid item xs={12} lg={6}>
                                    {isTournamentSession ? (
                                        <Select
                                            onSelectChange={handleOnTournamentTeamsChange}
                                            isRequired
                                            disabled={isRestrictedSession}
                                            hasError={!!errors.teamsMax}
                                            name="teamsMax"
                                            label="Max Teams"
                                            labelId="tournament-max-teams-label"
                                            options={tournamentTeamCountOptions}
                                            control={control}
                                            isFullWidth
                                        />
                                    ) : (
                                        <Input
                                            isRequired
                                            disabled={isRestrictedSession}
                                            name="teamsMax"
                                            label="Max Teams"
                                            type="number"
                                            hasError={!!errors.teamsMax}
                                            inputProps={{ min: 0 }}
                                            control={control}
                                        />
                                    )}
                                </Grid>
                                {!isTournamentSession && (
                                    <Grid item xs={12} lg={6}>
                                        <Input
                                            disabled={isRestrictedSession}
                                            isRequired
                                            name="matchesPerTeam"
                                            label="Matches Per Team"
                                            type="number"
                                            hasError={!!errors.matchesPerTeam}
                                            inputProps={{ min: 0 }}
                                            control={control}
                                            onChange={handleMatchesPerTeamCheck}
                                        />
                                        {errors.matchesPerTeam && (
                                            <Typography category={SECONDARY} size={X_SMALL_BOOK} color={theme.appColors.red}>
                                                {errors.matchesPerTeam.message}
                                            </Typography>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                        <Typography mb={2} category={SECONDARY} size={X_SMALL_MEDIUM_CAP}>
                            Player Fees
                        </Typography>
                        <Paper sx={{ p: 2, mb: 4, boxShadow: 'none' }}>
                            <Box mb={2} display="flex" justifyContent="flex-end">
                                <Box display="flex" alignItems="center" mr={2}>
                                    <Typography category={SECONDARY} size={MEDIUM_BOOK}>
                                        Session Player Entry Fee
                                    </Typography>
                                </Box>
                                <Controller
                                    name="registrationFee"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { onChange, value } }) => (
                                        <TextField
                                            disabled={hasRegisteredPlayers}
                                            value={value}
                                            variant="filled"
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            }}
                                            inputProps={{ style: { textAlign: 'right', width: 55 } }}
                                            onChange={(e) => {
                                                onChange(e);
                                                handleEntryFeeChange(e);
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                            <Typography mr={1.2} align="right" category={SECONDARY} size={SMALL_BOOK}>
                                {`League Fee: ${formatMoney(leagueVerifiedFee)}`}
                            </Typography>
                            <Typography mr={1.2} align="right" category={SECONDARY} size={SMALL_BOOK}>
                                {`Total per Player: ${formatMoney(totalPlayerCost)}`}
                            </Typography>
                        </Paper>
                    </form>
                )}
                <Popup
                    onAction={handleOnConfirmRuleChange}
                    actionLabel="Yes, change"
                    buttonLabel="No, cancel"
                    open={showRuleConfirmationModal}
                    onClose={handleOnCloseRuleModal}
                    title="Confirm standings rule"
                    message={`Are you sure you want to change the team standings rule to ${SessionStandingsRules[updatedStandingsRuleId]}?`}
                />
            </Grid>
        </FullPageModal>
    );
};

ManageSessionModal.propTypes = { ...manageSessionModalPropTypes };

export default ManageSessionModal;
